import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MobileSelect,
  MobileSelectItem,
} from "@/components/ui/mobile-select";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useGovernorates, useDistricts } from "@/hooks/use-locations";
import type { Address, CreateAddressDto } from "@/lib/types";

interface AddressFormProps {
  address?: Address | null;
  onSubmit: (data: CreateAddressDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function AddressForm({ address, onSubmit, onCancel, isLoading }: AddressFormProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  const formSchema = z.object({
    label: z.string().min(1, t("addresses.validation.labelRequired")),
    governorateId: z.string().min(1, t("addresses.validation.governorateRequired")),
    districtId: z.string().min(1, t("addresses.validation.districtRequired")),
    block: z.string().min(1, t("addresses.validation.blockRequired")),
    avenue: z.string().optional(),
    street: z.string().min(1, t("addresses.validation.streetRequired")),
    building: z.string().min(1, t("addresses.validation.buildingRequired")),
    landmark: z.string().optional(),
    isDefault: z.boolean().default(false),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: address?.label || "",
      governorateId: address?.governorateId || "",
      districtId: address?.districtId || "",
      block: address?.block || "",
      avenue: address?.avenue || "",
      street: address?.street || "",
      building: address?.building || "",
      landmark: address?.landmark || "",
      isDefault: address?.isDefault || false,
    },
  });

  const { data: governorates, isLoading: governoratesLoading } = useGovernorates();
  const selectedGovernorateId = form.watch("governorateId");
  const { data: districts, isLoading: districtsLoading } = useDistricts(selectedGovernorateId);
  
  const prevGovernorateRef = useRef<string>("");

  useEffect(() => {
    if (selectedGovernorateId && prevGovernorateRef.current && prevGovernorateRef.current !== selectedGovernorateId) {
      form.setValue("districtId", "");
    }
    prevGovernorateRef.current = selectedGovernorateId;
  }, [selectedGovernorateId, form]);

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    onSubmit({
      label: data.label,
      governorateId: data.governorateId,
      districtId: data.districtId,
      block: data.block,
      avenue: data.avenue || undefined,
      street: data.street,
      building: data.building,
      landmark: data.landmark || undefined,
      isDefault: data.isDefault,
    });
  };

  const labelSuggestions = [
    { value: "home", label: t("addresses.labels.home") },
    { value: "office", label: t("addresses.labels.office") },
    { value: "other", label: t("addresses.labels.other") },
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="label"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("addresses.label")}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder={t("addresses.labelPlaceholder")}
                  className={cn("rounded-xl h-12", isRtl && "text-right")}
                  dir={isRtl ? "rtl" : "ltr"}
                  data-testid="input-address-label"
                />
              </FormControl>
              <div className="flex gap-2 flex-wrap mt-2">
                {labelSuggestions.map((suggestion) => (
                  <Button
                    key={suggestion.value}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => form.setValue("label", suggestion.label)}
                    className="text-xs"
                  >
                    {suggestion.label}
                  </Button>
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className={cn("grid md:grid-cols-2 gap-4", isRtl && "md:grid-flow-dense")}>
          <FormField
            control={form.control}
            name="governorateId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("booking.governorate")}</FormLabel>
                <FormControl>
                  <MobileSelect
                    onValueChange={field.onChange}
                    value={field.value}
                    placeholder={t("booking.selectGovernorate")}
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    data-testid="select-governorate"
                  >
                    {governoratesLoading ? (
                      <option disabled>{t("booking.loading")}</option>
                    ) : governorates?.length === 0 ? (
                      <option disabled>{t("booking.noGovernorates")}</option>
                    ) : (
                      governorates?.map((gov) => (
                        <MobileSelectItem key={gov.id} value={gov.id}>
                          {isRtl ? gov.nameAr : gov.nameEn}
                        </MobileSelectItem>
                      ))
                    )}
                  </MobileSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="districtId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("booking.district")}</FormLabel>
                <FormControl>
                  <MobileSelect
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!selectedGovernorateId}
                    placeholder={selectedGovernorateId ? t("booking.selectDistrict") : t("booking.selectGovernorateFirst")}
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    data-testid="select-district"
                  >
                    {districtsLoading ? (
                      <option disabled>{t("booking.loading")}</option>
                    ) : districts?.length === 0 ? (
                      <option disabled>{t("booking.noDistricts")}</option>
                    ) : (
                      districts?.map((district) => (
                        <MobileSelectItem key={district.id} value={district.id}>
                          {isRtl ? district.nameAr : district.nameEn}
                        </MobileSelectItem>
                      ))
                    )}
                  </MobileSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className={cn("grid grid-cols-4 gap-4", isRtl && "grid-flow-dense")}>
          <FormField
            control={form.control}
            name="block"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("addresses.block")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="12"
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid="input-block"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="street"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("addresses.street")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="5"
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid="input-street"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="avenue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("addresses.avenue")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Gulf Road"
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid="input-avenue"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="building"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("addresses.building")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="45"
                    className={cn("rounded-xl h-12", isRtl && "text-right")}
                    dir={isRtl ? "rtl" : "ltr"}
                    data-testid="input-building"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="landmark"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("addresses.landmark")}</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  placeholder={t("addresses.landmarkPlaceholder")}
                  className={cn("rounded-xl h-12", isRtl && "text-right")}
                  dir={isRtl ? "rtl" : "ltr"}
                  data-testid="input-landmark"
                />
              </FormControl>
              <FormDescription className="text-xs">{t("addresses.landmarkHelper")}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0 rtl:space-x-reverse">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="checkbox-default"
                />
              </FormControl>
              <FormLabel className="text-sm font-normal cursor-pointer">
                {t("addresses.setAsDefaultLabel")}
              </FormLabel>
            </FormItem>
          )}
        />

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 h-12 rounded-xl"
            data-testid="button-cancel"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            className="flex-1 h-12 rounded-xl"
            disabled={isLoading}
            data-testid="button-save-address"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : address ? (
              t("addresses.update")
            ) : (
              t("addresses.save")
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
